import { Alert, Box, Button, DataTable, Loader, Text } from '@tocktix/pantry';
import {
  BulkUserActionItemStatus,
  BulkUserActionStatus,
} from 'tools/bff/generated/user/user_administration_pb';
import { Code, ConnectError } from '@connectrpc/connect';
import { FunctionComponent, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { userClient } from 'tools/bff/client';

const POLL_INTERVAL_MS = 2000;
const EM_DASH = '\u2014';

const ITEM_STATUS_LABEL: Record<BulkUserActionItemStatus, string> = {
  [BulkUserActionItemStatus.UNSPECIFIED]: 'Pending',
  [BulkUserActionItemStatus.SUCCEEDED]: 'Deactivated',
  [BulkUserActionItemStatus.FAILED]: 'Failed',
  [BulkUserActionItemStatus.SKIPPED]: 'Skipped',
};

// Pantry ships no progress bar, so build a lightweight determinate track from --pantry-* tokens.
const trackStyles = css`
  width: 100%;
  height: 8px;
  border-radius: var(--pantry-borderRadius-round);
  background: var(--pantry-color-bgSubtle);
  overflow: hidden;
`;

const fillStyles = (pct: number) => css`
  width: ${pct}%;
  height: 100%;
  background: var(--pantry-color-bgSuccessStrong);
  transition: width 0.3s ease;
`;

interface BulkUserProgressProps {
  // True when the start response attached to an already-running job (the submitted batch was not
  // started); latched so the banner never disappears.
  attachedFromStart?: boolean;
  onStartAnother: () => void;
}

const BulkUserProgress: FunctionComponent<BulkUserProgressProps> = ({
  attachedFromStart = false,
  onStartAnother,
}) => {
  const [status, setStatus] = useState<BulkUserActionStatus | null>(null);
  const [attachedLatched, setAttachedLatched] = useState(attachedFromStart);
  const [noActiveJob, setNoActiveJob] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);

  useEffect(() => {
    let active = true;
    const intervalId = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
    const stop = () => clearInterval(intervalId);

    async function poll() {
      try {
        const next = await userClient.getBulkUserActionStatus({});
        if (!active) {
          return;
        }
        setPollError(null);
        setStatus(next);
        if (next.attachedToExistingRun) {
          setAttachedLatched(true);
        }
        if (next.done) {
          stop();
        }
      } catch (err) {
        if (!active) {
          return;
        }
        // A missing workflow (grpc NOT_FOUND) means no active job for this operator — not an error.
        if (err instanceof ConnectError && err.code === Code.NotFound) {
          stop();
          setNoActiveJob(true);
        } else {
          setPollError('Error fetching bulk action status. Retrying...');
        }
      }
    }

    void poll();
    return () => {
      active = false;
      stop();
    };
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await userClient.cancelBulkUserAction({ cancelAll: true });
      setCancelRequested(true);
    } catch {
      setPollError('Failed to request cancellation. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (noActiveJob && status === null) {
    return (
      <Box display="flex" flexDirection="column" gap={300} data-testid="bulk-user-no-active-job">
        <Text variant="body" color="fgSubtle">
          No active bulk job for your account.
        </Text>
        <Box>
          <Button
            label="Back to start"
            onClick={onStartAnother}
            data-testid="bulk-user-back-to-start-button"
          />
        </Box>
      </Box>
    );
  }

  const isDone = status?.done ?? false;
  const total = status?.numTotal ?? 0;
  const processed = status?.numProcessed ?? 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <Box display="flex" flexDirection="column" gap={300}>
      {attachedLatched && (
        <Alert
          color="warning"
          title="A bulk job is already running for your account"
          message="Attaching to the existing job — the batch you just submitted was not started. Only one bulk action can run per operator at a time."
          data-testid="bulk-user-attached-banner"
        />
      )}
      {pollError && (
        <Alert color="warning" message={pollError} data-testid="bulk-user-poll-error" />
      )}

      {status === null ? (
        <Box padding={300} display="flex" justifyContent="center">
          <Loader aria-label="Starting bulk action" size="md" />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={200}>
          <Text variant="bodyStrong" data-testid="bulk-user-progress-count">
            {processed} / {total} processed
          </Text>
          <div
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            css={trackStyles}
            data-testid="bulk-user-progress-bar"
          >
            <div css={fillStyles(percent)} />
          </div>
          <Box display="flex" gap={300}>
            <Text variant="bodySm" color="fgSuccessBase" data-testid="bulk-user-success-count">
              {status.numSuccess} succeeded
            </Text>
            <Text variant="bodySm" color="fgDangerBase" data-testid="bulk-user-failed-count">
              {status.numFailed} failed
            </Text>
          </Box>
        </Box>
      )}

      {isDone && status !== null ? (
        <Box display="flex" flexDirection="column" gap={200}>
          <Text variant="h5" as="h2">
            Results
          </Text>
          {status.results.length === 0 ? (
            <Text variant="body" color="fgSubtle">
              No per-user results were returned.
            </Text>
          ) : (
            <DataTable id="bulk-user-results-table">
              <DataTable.HeaderRow>
                <DataTable.Header id="uuid" title="User UUID" />
                <DataTable.Header id="status" title="Status" />
                <DataTable.Header id="error" title="Error" />
              </DataTable.HeaderRow>
              <DataTable.Body>
                {status.results.map((result, index) => (
                  <DataTable.Row key={`${result.uuid}-${index}`} id={index} index={index}>
                    <DataTable.Cell columnId="uuid">{result.uuid}</DataTable.Cell>
                    <DataTable.Cell columnId="status">
                      {ITEM_STATUS_LABEL[result.status]}
                    </DataTable.Cell>
                    <DataTable.Cell columnId="error">{result.error ?? EM_DASH}</DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable>
          )}
          {status.numTruncatedResults > 0 && (
            <Text variant="caption" color="fgSubtle" data-testid="bulk-user-truncated-note">
              {status.numTruncatedResults} additional result(s) were truncated.
            </Text>
          )}
          <Box>
            <Button
              label="Start another batch"
              onClick={onStartAnother}
              data-testid="bulk-user-start-another-button"
            />
          </Box>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={100}>
          <Box>
            <Button
              variant="outlined"
              color="danger"
              label={cancelRequested ? 'Cancellation requested' : 'Cancel bulk action'}
              onClick={handleCancel}
              disabled={cancelling || cancelRequested}
              data-testid="bulk-user-cancel-button"
            />
          </Box>
          <Text variant="caption" color="fgSubtle">
            Closing this page does not cancel the job — it keeps running server-side and you can
            reattach by reopening this page.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default BulkUserProgress;
